import { SeoPanel } from '@/features/sales/extras-pages';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CalendarDays, CheckCircle2, Copy, Download, ExternalLink, Eye, Loader2, MapPin, Newspaper, Plus, Sparkles, Trash2, Users, Video } from 'lucide-react';
import { PageHeader, EmptyState } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Brand } from '@/components/shared/brand';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, Switch, Textarea } from '@/components/ui/misc';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PageLoader } from '@/components/ui/loader';
import { useAiStatus } from '@/features/ai/ai';
import { ApiRequestError, apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/format';
import { Markdown } from '@/lib/markdown';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';

const failed = (error: unknown, fallback: string) => toast.error(error instanceof ApiRequestError ? error.message : fallback);
const copy = (text: string, done: string) => {
  void navigator.clipboard.writeText(text);
  toast.success(done);
};

// ══ Blog ═════════════════════════════════════════════════════════════════════

type Post = {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  body: string;
  coverUrl: string | null;
  tags: string[];
  status: 'DRAFT' | 'PUBLISHED' | 'SCHEDULED';
  publishAt: string | null;
  publishedAt: string | null;
  seoTitle: string | null;
  seoDescription: string | null;
  viewCount: number;
  updatedAt: string;
  url?: string;
};

const STATUS_VARIANT = { DRAFT: 'secondary', PUBLISHED: 'success', SCHEDULED: 'warning' } as const;

export function BlogPage() {
  const t = useT();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ['blog'], queryFn: () => apiGet<{ blogUrl: string; posts: Post[] }>('/blog-posts') });
  const create = useMutation({
    mutationFn: () => apiPost<Post>('/blog-posts', { title: t('Untitled post'), body: '' }),
    onSuccess: (post) => navigate(`/marketing/blog/${post.id}`),
    onError: (error) => failed(error, 'Could not create a post'),
  });
  return (
    <div>
      <PageHeader
        title="Blog"
        description="Articles on your public site, next to your help centre. Write in Markdown, let AI draft, publish now or schedule."
        actions={
          <>
            {data?.blogUrl && (
              <Button asChild variant="outline">
                <a href={data.blogUrl} target="_blank" rel="noreferrer">
                  <ExternalLink /> {t('View blog')}
                </a>
              </Button>
            )}
            <Can permission="marketing.blog.create">
              <Button onClick={() => create.mutate()} disabled={create.isPending}>
                <Plus /> {t('New post')}
              </Button>
            </Can>
          </>
        }
      />
      {isLoading ? (
        <PageLoader />
      ) : !data?.posts.length ? (
        <EmptyState icon={<Newspaper className="h-6 w-6" />} title="No posts yet" description="Share news, guides and market updates. Each post gets its own page your customers can find." />
      ) : (
        <div className="overflow-hidden rounded-lg border bg-card">
          {data.posts.map((post) => (
            <Link key={post.id} to={`/marketing/blog/${post.id}`} className="flex items-center gap-4 border-b px-4 py-3 last:border-0 hover:bg-muted/40">
              <div className="h-12 w-20 shrink-0 overflow-hidden rounded bg-muted">{post.coverUrl && <img src={post.coverUrl} alt="" className="h-full w-full object-cover" />}</div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold">{post.title}</p>
                <p className="truncate text-xs text-muted-foreground">{post.excerpt || post.tags.join(', ') || `/${post.slug}`}</p>
              </div>
              <Badge variant={STATUS_VARIANT[post.status]}>{t(post.status.toLowerCase().replace(/^\w/, (c) => c.toUpperCase()))}</Badge>
              <span className="hidden w-28 text-end text-xs text-muted-foreground sm:block">
                {post.status === 'SCHEDULED' && post.publishAt ? formatDateTime(post.publishAt) : post.publishedAt ? formatDate(post.publishedAt) : formatDate(post.updatedAt)}
              </span>
              <span className="hidden w-14 items-center justify-end gap-1 text-xs text-muted-foreground md:flex">
                <Eye className="h-3.5 w-3.5" /> {post.viewCount}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function BlogEditorPage() {
  const t = useT();
  const { postId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: post, isLoading } = useQuery({ queryKey: ['blog', postId], queryFn: () => apiGet<Post>(`/blog-posts/${postId}`) });
  const { data: ai } = useAiStatus();
  const [form, setForm] = useState<Post | null>(null);
  const [tab, setTab] = useState<'write' | 'preview'>('write');
  useEffect(() => {
    if (post && !form) setForm(post);
  }, [post, form]);
  const save = useMutation({
    mutationFn: (patch: Partial<Post>) => apiPatch<Post>(`/blog-posts/${postId}`, patch),
    onSuccess: (saved) => {
      setForm(saved);
      void queryClient.invalidateQueries({ queryKey: ['blog'] });
      toast.success(saved.status === 'PUBLISHED' ? t('Published') : saved.status === 'SCHEDULED' ? t('Scheduled') : t('Saved'));
    },
    onError: (error) => failed(error, 'Could not save'),
  });
  const draft = useMutation({
    mutationFn: () => apiPost<{ body: string }>('/ai/article', { title: form?.title ?? '', notes: form?.excerpt ?? undefined }),
    onSuccess: (result) => form && setForm({ ...form, body: result.body }),
    onError: (error) => failed(error, 'AI could not draft the post'),
  });
  const remove = useMutation({
    mutationFn: () => apiDelete(`/blog-posts/${postId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['blog'] });
      navigate('/marketing/blog');
    },
  });
  if (isLoading || !form) return <PageLoader />;
  const set = (patch: Partial<Post>) => setForm({ ...form, ...patch });
  const fields = { title: form.title, slug: form.slug, excerpt: form.excerpt, body: form.body, coverUrl: form.coverUrl ?? '', tags: form.tags, seoTitle: form.seoTitle, seoDescription: form.seoDescription };
  return (
    <div>
      <PageHeader
        title={form.title || t('Untitled post')}
        breadcrumbs={[{ label: 'Blog', to: '/marketing/blog' }, { label: form.title }]}
        actions={
          <>
            {form.status === 'PUBLISHED' && form.url && (
              <Button asChild variant="outline">
                <a href={form.url} target="_blank" rel="noreferrer">
                  <ExternalLink /> {t('View')}
                </a>
              </Button>
            )}
            <Button variant="outline" onClick={() => save.mutate({ ...fields, status: 'DRAFT' })} disabled={save.isPending}>
              {t('Save draft')}
            </Button>
            <Button onClick={() => save.mutate({ ...fields, status: 'PUBLISHED' })} disabled={save.isPending}>
              {t('Publish')}
            </Button>
          </>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[1fr_18rem]">
        <div className="space-y-3">
          <Input className="h-12 text-lg font-bold" value={form.title} onChange={(event) => set({ title: event.target.value })} placeholder={t('Post title')} />
          <div className="rounded-lg border bg-card">
            <div className="flex items-center gap-1 border-b p-1.5">
              {(['write', 'preview'] as const).map((key) => (
                <button key={key} type="button" onClick={() => setTab(key)} className={cn('rounded px-3 py-1 text-sm font-semibold', tab === key ? 'bg-muted' : 'text-muted-foreground')}>
                  {key === 'write' ? t('Write') : t('Preview')}
                </button>
              ))}
              {ai?.enabled && (
                <Button size="sm" variant="ghost" className="ms-auto" onClick={() => draft.mutate()} disabled={draft.isPending || !form.title}>
                  {draft.isPending ? <Loader2 className="animate-spin" /> : <Sparkles />} {t('Draft with AI')}
                </Button>
              )}
            </div>
            {tab === 'write' ? (
              <Textarea rows={22} className="rounded-none border-0 font-mono text-sm focus-visible:ring-0" value={form.body} onChange={(event) => set({ body: event.target.value })} placeholder={t('Write in Markdown: ## headings, **bold**, - lists, [links](https://…)')} />
            ) : (
              <div className="prose prose-sm max-w-none p-5 dark:prose-invert">
                <Markdown source={form.body || '_Nothing written yet._'} />
              </div>
            )}
          </div>
        </div>
        <div className="space-y-3">
          <div className="space-y-3 rounded-lg border bg-card p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold">{t('Status')}</span>
              <Badge variant={STATUS_VARIANT[form.status]}>{t(form.status.toLowerCase().replace(/^\w/, (c) => c.toUpperCase()))}</Badge>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('Schedule for')}</Label>
              <div className="flex gap-1.5">
                <Input type="datetime-local" className="h-8" value={form.publishAt ? form.publishAt.slice(0, 16) : ''} onChange={(event) => set({ publishAt: event.target.value })} />
                <Button size="sm" variant="outline" disabled={!form.publishAt || save.isPending} onClick={() => save.mutate({ ...fields, status: 'SCHEDULED', publishAt: form.publishAt })}>
                  {t('Schedule')}
                </Button>
              </div>
            </div>
          </div>
          <div className="space-y-3 rounded-lg border bg-card p-4">
            <div className="space-y-1">
              <Label className="text-xs">{t('Web address')}</Label>
              <Input className="h-8" value={form.slug} onChange={(event) => set({ slug: event.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t('Summary')}</Label>
              <Textarea rows={3} value={form.excerpt ?? ''} onChange={(event) => set({ excerpt: event.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t('Cover image URL')}</Label>
              <Input className="h-8" value={form.coverUrl ?? ''} onChange={(event) => set({ coverUrl: event.target.value })} placeholder="https://…" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t('Tags, comma separated')}</Label>
              <Input className="h-8" value={form.tags.join(', ')} onChange={(event) => set({ tags: event.target.value.split(',').map((tag) => tag.trim()).filter(Boolean) })} />
            </div>
          </div>
          <SeoPanel kind="post" id={form.id} />
          <div className="space-y-3 rounded-lg border bg-card p-4">
            <p className="text-sm font-bold">SEO</p>
            <Input className="h-8" value={form.seoTitle ?? ''} onChange={(event) => set({ seoTitle: event.target.value })} placeholder={t('Search title (up to 70 characters)')} maxLength={70} />
            <Textarea rows={2} value={form.seoDescription ?? ''} onChange={(event) => set({ seoDescription: event.target.value })} placeholder={t('Search description (up to 170 characters)')} maxLength={170} />
          </div>
          <Can permission="marketing.blog.delete">
            <button type="button" className="inline-flex items-center gap-1 text-sm text-destructive hover:underline" onClick={() => window.confirm(t('Delete this post?')) && remove.mutate()}>
              <Trash2 className="h-4 w-4" /> {t('Delete post')}
            </button>
          </Can>
        </div>
      </div>
    </div>
  );
}

/** /blog/:slug and /blog/:slug/:post — the public blog. */
export function PublicBlogPage() {
  const { slug = '', post: postSlug } = useParams();
  const list = useQuery({ queryKey: ['public-blog', slug], queryFn: () => apiGet<{ business: string; posts: { title: string; slug: string; excerpt: string | null; coverUrl: string | null; tags: string[]; publishedAt: string }[] }>(`/public/blog/${slug}`), enabled: !postSlug, retry: false });
  const post = useQuery({
    queryKey: ['public-blog', slug, postSlug],
    queryFn: () => apiGet<{ business: string; title: string; body: string; coverUrl: string | null; tags: string[]; publishedAt: string; seoTitle: string | null; seoDescription: string | null }>(`/public/blog/${slug}/${postSlug}`),
    enabled: Boolean(postSlug),
    retry: false,
  });
  useEffect(() => {
    if (post.data) document.title = post.data.seoTitle || `${post.data.title} — ${post.data.business}`;
    else if (list.data) document.title = `Blog — ${list.data.business}`;
  }, [post.data, list.data]);
  const business = post.data?.business ?? list.data?.business ?? '';
  return (
    <div className="min-h-screen bg-white text-slate-900" style={{ colorScheme: 'light' }}>
      <header className="border-b border-slate-200">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-4">
          <Link to={`/blog/${slug}`} className="text-lg font-bold">
            {business} <span className="font-normal text-slate-500">Blog</span>
          </Link>
          <Link to={`/help/${slug}`} className="text-sm font-semibold text-slate-600 hover:text-slate-900">
            Help centre
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-4 py-10">
        {(list.isLoading || post.isLoading) && <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />}
        {(list.isError || post.isError) && <p className="text-center text-slate-500">This page could not be found.</p>}
        {post.data && (
          <article>
            {post.data.coverUrl && <img src={post.data.coverUrl} alt="" className="mb-8 aspect-[2/1] w-full rounded-2xl object-cover" />}
            <p className="text-sm text-slate-500">{formatDate(post.data.publishedAt)}</p>
            <h1 className="mt-2 text-4xl font-bold leading-tight tracking-tight">{post.data.title}</h1>
            <div className="prose prose-slate mt-8 max-w-none">
              <Markdown source={post.data.body} />
            </div>
            {post.data.tags.length > 0 && (
              <div className="mt-8 flex flex-wrap gap-2">
                {post.data.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </article>
        )}
        {list.data && (
          <div className="grid gap-6 sm:grid-cols-2">
            {list.data.posts.length === 0 && <p className="text-slate-500">No posts yet.</p>}
            {list.data.posts.map((item) => (
              <Link key={item.slug} to={`/blog/${slug}/${item.slug}`} className="group overflow-hidden rounded-2xl border border-slate-200 transition-shadow hover:shadow-lg">
                <div className="aspect-[2/1] bg-slate-100">{item.coverUrl && <img src={item.coverUrl} alt="" className="h-full w-full object-cover" />}</div>
                <div className="p-5">
                  <p className="text-xs text-slate-500">{formatDate(item.publishedAt)}</p>
                  <h2 className="mt-1 text-lg font-bold group-hover:text-primary">{item.title}</h2>
                  {item.excerpt && <p className="mt-2 line-clamp-3 text-sm text-slate-600">{item.excerpt}</p>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <footer className="flex items-center justify-center gap-1.5 py-8 text-xs text-slate-400">
        Powered by <Brand size="sm" className="scale-75" />
      </footer>
    </div>
  );
}

// ══ Events ═══════════════════════════════════════════════════════════════════

type EventRow = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  type: 'WEBINAR' | 'IN_PERSON' | 'HYBRID';
  startsAt: string;
  endsAt: string | null;
  location: string | null;
  joinUrl: string | null;
  capacity: number | null;
  isPublished: boolean;
  url: string;
  registered?: number;
  attended?: number;
  registrations?: { id: string; name: string; email: string; phone: string | null; company: string | null; status: string; createdAt: string; contactId: string | null }[];
};

const TYPE_LABEL = { WEBINAR: 'Webinar', IN_PERSON: 'In person', HYBRID: 'Hybrid' } as const;

export function EventsPage() {
  const t = useT();
  const { data: events = [], isLoading } = useQuery({ queryKey: ['events'], queryFn: () => apiGet<EventRow[]>('/marketing-events') });
  const [creating, setCreating] = useState(false);
  const now = Date.now();
  return (
    <div>
      <PageHeader
        title="Events"
        description="Webinars, open houses and launches with a registration page. Every registrant becomes a contact; mark who attended."
        actions={
          <Can permission="marketing.event.create">
            <Button onClick={() => setCreating(true)}>
              <Plus /> {t('Create event')}
            </Button>
          </Can>
        }
      />
      {isLoading ? (
        <PageLoader />
      ) : events.length === 0 ? (
        <EmptyState icon={<CalendarDays className="h-6 w-6" />} title="No events yet" description="Create a webinar or an open house and share its registration link." />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {events.map((event) => (
            <Link key={event.id} to={`/marketing/events/${event.id}`} className={cn('rounded-lg border bg-card p-4 hover:border-primary/50', new Date(event.startsAt).getTime() < now && 'opacity-70')}>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{t(TYPE_LABEL[event.type])}</Badge>
                {!event.isPublished && <Badge variant="warning">{t('Hidden')}</Badge>}
              </div>
              <p className="mt-2 font-bold">{event.name}</p>
              <p className="text-sm text-muted-foreground">{formatDateTime(event.startsAt)}</p>
              <p className="mt-3 flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1">
                  <Users className="h-4 w-4 text-muted-foreground" /> {event.registered} {t('registered')}
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="h-4 w-4 text-muted-foreground" /> {event.attended} {t('attended')}
                </span>
              </p>
            </Link>
          ))}
        </div>
      )}
      {creating && <EventDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

function EventDialog({ event, onClose }: { event?: EventRow; onClose: () => void }) {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: event?.name ?? '',
    description: event?.description ?? '',
    type: event?.type ?? ('IN_PERSON' as EventRow['type']),
    startsAt: event ? event.startsAt.slice(0, 16) : '',
    endsAt: event?.endsAt ? event.endsAt.slice(0, 16) : '',
    location: event?.location ?? '',
    joinUrl: event?.joinUrl ?? '',
    capacity: event?.capacity ? String(event.capacity) : '',
    isPublished: event?.isPublished ?? true,
  });
  const save = useMutation({
    mutationFn: () => {
      const body = { ...form, startsAt: new Date(form.startsAt).toISOString(), endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null, capacity: form.capacity ? Number(form.capacity) : null };
      return event ? apiPatch<EventRow>(`/marketing-events/${event.id}`, body) : apiPost<EventRow>('/marketing-events', body);
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['events'] });
      onClose();
      if (!event) navigate(`/marketing/events/${saved.id}`);
    },
    onError: (error) => failed(error, 'Could not save the event'),
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{event ? t('Edit event') : t('Create event')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('Event name')} />
          <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as EventRow['type'] })}>
            {Object.entries(TYPE_LABEL).map(([key, label]) => (
              <option key={key} value={key}>
                {t(label)}
              </option>
            ))}
          </Select>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">{t('Starts')}</Label>
              <Input type="datetime-local" value={form.startsAt} onChange={(e) => setForm({ ...form, startsAt: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t('Ends')}</Label>
              <Input type="datetime-local" value={form.endsAt} onChange={(e) => setForm({ ...form, endsAt: e.target.value })} />
            </div>
          </div>
          {form.type !== 'WEBINAR' && <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder={t('Venue, e.g. Address Downtown, Dubai')} />}
          {form.type !== 'IN_PERSON' && <Input value={form.joinUrl} onChange={(e) => setForm({ ...form, joinUrl: e.target.value })} placeholder={t('Join link (Zoom, Teams, Meet)')} />}
          <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t('What the event is about')} />
          <div className="flex items-center gap-3">
            <Input type="number" min={1} className="w-40" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} placeholder={t('Capacity')} />
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.isPublished} onCheckedChange={(value) => setForm({ ...form, isPublished: value })} /> {t('Registration open')}
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => save.mutate()} disabled={!form.name.trim() || !form.startsAt || save.isPending}>
            {t('Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EventDetailPage() {
  const t = useT();
  const { eventId = '' } = useParams();
  const queryClient = useQueryClient();
  const { data: event, isLoading } = useQuery({ queryKey: ['events', eventId], queryFn: () => apiGet<EventRow>(`/marketing-events/${eventId}`) });
  const [editing, setEditing] = useState(false);
  const mark = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => apiPatch(`/marketing-events/${eventId}/registrations/${id}`, { status }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['events', eventId] }),
    onError: (error) => failed(error, 'Could not update'),
  });
  if (isLoading || !event) return <PageLoader />;
  const rows = event.registrations ?? [];
  const exportCsv = () => {
    const csv = ['Name,Email,Phone,Company,Status,Registered', ...rows.map((row) => [row.name, row.email, row.phone ?? '', row.company ?? '', row.status, row.createdAt].map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))].join('\n');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob(['﻿', csv], { type: 'text/csv' }));
    link.download = `${event.slug}-registrations.csv`;
    link.click();
  };
  return (
    <div>
      <PageHeader
        title={event.name}
        breadcrumbs={[{ label: 'Events', to: '/marketing/events' }, { label: event.name }]}
        description={`${formatDateTime(event.startsAt)} · ${t(TYPE_LABEL[event.type])}${event.location ? ` · ${event.location}` : ''}`}
        actions={
          <>
            <Button variant="outline" onClick={() => copy(event.url, t('Registration link copied'))}>
              <Copy /> {t('Copy registration link')}
            </Button>
            <Can permission="marketing.event.update">
              <Button variant="outline" onClick={() => setEditing(true)}>
                {t('Edit')}
              </Button>
            </Can>
          </>
        }
      />
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        {[
          ['Registered', rows.filter((row) => row.status !== 'CANCELLED').length],
          ['Attended', rows.filter((row) => row.status === 'ATTENDED').length],
          ['Capacity', event.capacity ?? '∞'],
        ].map(([label, value]) => (
          <div key={String(label)} className="rounded-lg border bg-card p-4">
            <p className="text-sm text-muted-foreground">{t(String(label))}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
        ))}
      </div>
      <div className="overflow-hidden rounded-lg border bg-card">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <p className="font-bold">{t('Registrations')}</p>
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={!rows.length}>
            <Download /> {t('Export')}
          </Button>
        </div>
        {rows.length === 0 ? (
          <p className="p-8 text-center text-sm text-muted-foreground">{t('No registrations yet. Share the link.')}</p>
        ) : (
          rows.map((row) => (
            <div key={row.id} className="flex flex-wrap items-center gap-3 border-b px-4 py-2.5 text-sm last:border-0">
              <span className="min-w-0 flex-1">
                {row.contactId ? (
                  <Link to={`/crm/contacts/${row.contactId}`} className="text-link font-semibold hover:underline">
                    {row.name}
                  </Link>
                ) : (
                  <span className="font-semibold">{row.name}</span>
                )}
                <span className="block truncate text-xs text-muted-foreground">
                  {row.email}
                  {row.company ? ` · ${row.company}` : ''}
                </span>
              </span>
              <Select className="h-8 w-auto text-xs" value={row.status} onChange={(e) => mark.mutate({ id: row.id, status: e.target.value })}>
                {['REGISTERED', 'ATTENDED', 'NO_SHOW', 'CANCELLED'].map((status) => (
                  <option key={status} value={status}>
                    {t(status.replace('_', ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase()))}
                  </option>
                ))}
              </Select>
            </div>
          ))
        )}
      </div>
      {editing && <EventDialog event={event} onClose={() => { setEditing(false); void queryClient.invalidateQueries({ queryKey: ['events', eventId] }); }} />}
    </div>
  );
}

/** /e/:slug — public registration. */
export function PublicEventPage() {
  const { slug = '' } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-event', slug],
    queryFn: () => apiGet<{ name: string; description: string | null; type: EventRow['type']; startsAt: string; endsAt: string | null; timezone: string; location: string | null; business: string; full: boolean; past: boolean }>(`/public/events/${slug}`),
    retry: false,
  });
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '' });
  const register = useMutation({
    mutationFn: () => apiPost<{ when: string; joinUrl: string | null; location: string | null }>(`/public/events/${slug}/register`, { ...form, source: new URLSearchParams(window.location.search).get('utm_source') ?? undefined }),
    onError: (error) => failed(error, 'Could not register'),
  });
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#fff6f2] px-4 py-10" style={{ colorScheme: 'light' }}>
      <div className="w-full max-w-lg rounded-2xl border bg-white p-7 text-slate-900 shadow-brand-lg">
        {isLoading ? (
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
        ) : isError || !data ? (
          <p className="text-center text-slate-600">This event is not open for registration.</p>
        ) : register.isSuccess ? (
          <div className="py-4 text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
            <p className="mt-3 text-xl font-bold">You are registered!</p>
            <p className="mt-1 text-slate-600">{register.data.when}</p>
            {register.data.joinUrl && <p className="mt-1 text-sm text-slate-600">The join link is in your confirmation email.</p>}
          </div>
        ) : (
          <>
            <p className="text-sm font-semibold text-slate-500">{data.business}</p>
            <h1 className="mt-1 text-2xl font-bold">{data.name}</h1>
            <div className="mt-3 space-y-1 text-sm text-slate-600">
              <p className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4" /> {new Date(data.startsAt).toLocaleString('en-AE', { timeZone: data.timezone, dateStyle: 'full', timeStyle: 'short' })}
              </p>
              {data.location && (
                <p className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> {data.location}
                </p>
              )}
              {data.type !== 'IN_PERSON' && (
                <p className="flex items-center gap-2">
                  <Video className="h-4 w-4" /> Online
                </p>
              )}
            </div>
            {data.description && <p className="mt-4 whitespace-pre-wrap text-slate-700">{data.description}</p>}
            {data.past ? (
              <p className="mt-6 rounded-lg bg-slate-100 p-3 text-sm">This event has already taken place.</p>
            ) : data.full ? (
              <p className="mt-6 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">Sorry, this event is full.</p>
            ) : (
              <form
                className="mt-6 space-y-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  register.mutate();
                }}
              >
                {(['name', 'email', 'phone', 'company'] as const).map((key) => (
                  <input
                    key={key}
                    required={key === 'name' || key === 'email'}
                    type={key === 'email' ? 'email' : key === 'phone' ? 'tel' : 'text'}
                    value={form[key]}
                    onChange={(event) => setForm({ ...form, [key]: event.target.value })}
                    placeholder={{ name: 'Full name', email: 'Email', phone: 'Phone (optional)', company: 'Company (optional)' }[key]}
                    className="h-11 w-full rounded-lg border border-slate-200 px-3 outline-none focus:border-primary"
                  />
                ))}
                <Button type="submit" className="h-11 w-full" disabled={register.isPending}>
                  {register.isPending ? <Loader2 className="animate-spin" /> : null} Register
                </Button>
              </form>
            )}
          </>
        )}
        <div className="mt-8 flex items-center justify-center gap-1.5 text-xs text-slate-400">
          Powered by <Brand size="sm" className="scale-75" />
        </div>
      </div>
    </div>
  );
}
